import React from "react";
import { Phone, Mail, Clock, Sun } from "lucide-react";
import { useGoTo } from "../nav";
import { useLang } from "../i18n";
import { LogoBadge, Rainbow, Cloud, Flower } from "./decor";

const EMAIL = "Nenisplayhousechildcare@gmail.com";

const LINKS = [
  { key: "home", tid: "home", href: "/" },
  { key: "about", tid: "about-us", href: "#about" },
  { key: "programs", tid: "our-programs", href: "#programs" },
  { key: "gallery", tid: "gallery", href: "/gallery" },
  { key: "contact", tid: "contact", href: "/contact" },
];

const ChipRow = ({ icon: Icon, label, children, testid }) => (
  <div className="flex items-start gap-4" data-testid={testid}>
    <span className="w-11 h-11 shrink-0 rounded-2xl bg-cardGreen flex items-center justify-center">
      <Icon className="w-5 h-5 text-white" />
    </span>
    <div>
      <p className="font-display font-semibold text-white">{label}</p>
      <div className="text-white/85 text-[15px] leading-relaxed">{children}</div>
    </div>
  </div>
);

export default function Footer() {
  const goTo = useGoTo();
  const { s } = useLang();
  return (
    <footer className="relative bg-coral pt-16 pb-8 overflow-hidden" data-testid="site-footer">
      <Rainbow className="absolute left-[4%] top-8 w-20 opacity-70" />
      <Cloud className="absolute right-[6%] top-10 w-24 opacity-80" />
      <Flower className="absolute left-[16%] bottom-24 w-8 animate-sway" petal="#FFF8F0" center="#FFD97A" />
      <Cloud className="absolute right-[28%] bottom-16 w-16 opacity-50" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="text-center lg:text-left flex flex-col items-center lg:items-start gap-3">
            <LogoBadge size={112} />
            <h2 className="font-display font-bold text-white text-4xl sm:text-5xl mt-4">{s.footer.h}</h2>
            <p className="font-display font-semibold text-white text-lg">Neni’s Playhouse Childcare</p>
            <p className="text-white/85 text-[15px] leading-relaxed max-w-md mx-auto lg:mx-0">
              {s.footer.blurb}
            </p>
          </div>

          <div className="space-y-5">
            <ChipRow icon={Phone} label={s.footer.chips.call} testid="footer-phone-row">
              <a
                href="tel:+19174960201"
                data-testid="footer-phone-link"
                className="hover:text-white hover:underline underline-offset-2 font-bold"
              >
                +1 (917) 496-0201
              </a>
            </ChipRow>
            <ChipRow icon={Mail} label={s.footer.chips.email} testid="footer-email-row">
              <a
                href={`mailto:${EMAIL}`}
                data-testid="footer-email-link"
                className="hover:text-white hover:underline underline-offset-2 break-all"
              >
                {EMAIL}
              </a>
            </ChipRow>
            <ChipRow icon={Clock} label={s.footer.chips.hours} testid="footer-hours-row">
              {s.footer.hoursVal}
            </ChipRow>
            <ChipRow icon={Sun} label={s.footer.chips.flex} testid="footer-flex-row">
              {s.footer.flexVal}
            </ChipRow>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/25 flex flex-col sm:flex-row items-center justify-between gap-4">
          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {LINKS.map((l) => (
              <li key={l.key}>
                <a
                  data-testid={`footer-link-${l.tid}`}
                  href={l.href}
                  onClick={(e) => {
                    e.preventDefault();
                    goTo(l.href);
                  }}
                  className="font-display text-white/90 hover:text-white text-[15px]"
                >
                  {s.nav[l.key]}
                </a>
              </li>
            ))}
          </ul>
          <p className="text-white/70 text-[13px] text-center">
            © {new Date().getFullYear()} Neni’s Playhouse Childcare · happycampersdaycareny.com
          </p>
        </div>
      </div>
    </footer>
  );
}
